<template>
	<div class="nav menu-item">
		<ContextMenu
			:options="contextMenuOptions"
			v-if="contextMenuActive"
			ref="contextMenu"
			@onClose="closeContextMenu"
		/>
		<a
			:class="currentClassList"
			:href="menuLink"
			data-id="menu-item-link"
			@auxclick="handleAuxClick"
			@click="handleClick"
			ref="menuItemLink"
			@contextmenu="showContextMenu"
			:title="menuItem.itemText"
			@blur="closeContextMenu"
		>
			<span class="menu-item__link-icon">
				<i :class="['icon', iconClass]" :alt="menuItem.itemText" />
			</span>
			<span class="menu-item__link-label" data-id="menu-item-text">
				{{ menuItem.itemText }}
			</span>
			<span class="menu-item__expand-icon" v-if="hasSubMenu">
				<i class="icon icon-arrow_right_filled" />
			</span>
		</a>
		<Draggable
			tag="ul"
			class="menu-item__sub-menu"
			:class="isFavoritesParent && 'is-favorite-parent'"
			v-if="menuItem.childItems"
			data-id="child-items"
			:disabled="!isFavoritesParent"
			v-model="itemsInOrder"
			@change="onItemsOrderChange"
			v-bind="dragOptions"
			ref="subMenu"
		>
			<li
				class="menu-item__sub-menu-item"
				v-for="childItem in menuItem.childItems"
				:key="childItem.id"
			>
				<VueMenuItem
					:menuItem="childItem"
					:topLevelParentId="topLevelParentId"
					:parentMenuIds="[...parentMenuIds, menuItem.id]"
					@linkClick="onLinkClick"
					:isMobile="isMobile"
					:companyId="companyId"
				/>
			</li>
		</Draggable>
	</div>
</template>

<script>
import menuItemIcons from '../data/menu-item-icons';
import ContextMenu from '../components/shells/context-menu.vue';
import propsMixin from '../mixins/parent-component-props';
import { mapActions, mapGetters } from 'vuex';
import Draggable from 'vuedraggable';
import getMenuItemLink from '@/utils/get-menu-item-link';
import matchPathUtil from '@/utils/match-path';

export default {
	name: 'VueMenuItem',
	components: {
		ContextMenu,
		Draggable
	},
	data() {
		return {
			isSubMenuOpen: false,
			isOpen: false,
			openClass: 'is-open',
			activeClass: 'is-active',
			selectedRouteClass: 'is-selected-route',
			leafParentClass: 'is-leaf-parent',
			menuItemClass: 'menu-item__link',
			contextMenuActive: false,
			itemsInOrder: [],
			childRoutes: [],
			currentTranslateY: '',
			resizeId: null
		};
	},
	props: {
		menuItem: {
			type: Object,
			default: undefined
		},
		isTopLevel: {
			type: Boolean,
			default: false
		},
		companyId: {
			type: Number,
			default: undefined
		},
		topLevelParentId: {
			type: Number
		},
		isMobile: {
			type: Boolean,
			default: false
		},
		parentMenuIds: {
			type: Array,
			default: () => []
		}
	},
	mixins: [propsMixin],
	created() {
		this.reponsibleForUserInfo = true;
		const me = this;
		if (this.isFavoritesParent) {
			this.menuItem.childItems.forEach(element => {
				me.itemsInOrder.push({ ...element });
			});
		}
	},
	mounted() {
		document.addEventListener('click', this.handleDocumentClicked);
		document.addEventListener('auxclick', this.handleDocumentClicked);
		if (this.isMain || this.isSub) {
			window.addEventListener('resize', this.handleResize);
		}
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleDocumentClicked);
		document.removeEventListener('auxclick', this.handleDocumentClicked);
		if (this.isMain || this.isSub) {
			window.removeEventListener('resize', this.handleResize);
		}
	},
	computed: {
		...mapGetters('user', [
			'isFavorite',
			'mainItemClickedId',
			'lastItemClickedId',
			'appName',
			'vueAppBase',
			'currentRouteMenuId',
			'lastMainItemClickedId'
		]),
		pathMenuIds() {
			const ids = this.parentMenuIds;

			ids.push(this.menuItem.id);

			return ids;
		},
		menuLink() {
			let link = this.menuItem.itemValue;

			if (!this.isMain && !this.isSub) {
				link = getMenuItemLink(
					this.menuItem,
					this.companyId,
					this.vueAppBase
				);
			}

			return link;
		},
		menuLinkNoBase() {
			let link = this.menuItem.itemValue;

			if (!this.isMain && !this.isSub) {
				link = getMenuItemLink(this.menuItem, this.companyId);
			}

			return link;
		},
		isFavoritesParent() {
			return this.menuItem.itemText === 'Favorites';
		},
		contextMenuOptions() {
			const menuOptions = [
				{
					text: 'Open in a new tab',
					action: this.openInNewTab
				}
			];

			if (this.isFavorite(this.menuItem)) {
				menuOptions.push({
					text: 'Remove from Favorites',
					action: this.removeFromFavorites
				});
			} else {
				menuOptions.push({
					text: 'Add to Favorites',
					action: this.addToFavorites
				});
			}

			return menuOptions;
		},
		hasSubMenu() {
			if (this.menuItem.childItems.length > 0) {
				return true;
			}
			return false;
		},
		iconClass() {
			let className;

			if (this.menuItem.iconClass) {
				className = this.menuItem.iconClass;
			} else if (this.isTopLevel) {
				if (
					Object.prototype.hasOwnProperty.call(
						menuItemIcons,
						this.menuItem.itemText
					)
				) {
					className = menuItemIcons[this.menuItem.itemText];
				}
			}

			return className;
		},
		dragOptions() {
			return {
				animation: 200,
				group: 'description',
				ghostClass: 'sortable-ghost'
			};
		},
		currentClassList() {
			const classList = [this.menuItemClass];
			//let foundChildItem;

			if (this.isOpen || this.isTopLevel) {
				//only main sub menus can be open
				if (this.isOpen) {
					this.addClass(this.openClass, classList);

					if (this.menuItem.id === this.lastItemClickedId) {
						this.addClass(this.activeClass, classList);

						if (this.isMain || this.isSub) {
							this.addClass(this.leafParentClass, classList);
						}
					}

					if (this.isTopLevel) {
						this.addClass(this.selectedRouteClass, classList);
					}
				} else {
					this.removeClass(this.openClass, classList);
					this.removeClass(this.activeClass, classList);
					this.removeClass(this.leafParentClass, classList);

					if (this.menuItem.id === this.mainItemClickedId) {
						const childMatch = this.findChildItem(this.menuItem);

						if (childMatch) {
							this.addClass(this.selectedRouteClass, classList);
						} else {
							this.removeClass(
								this.selectedRouteClass,
								classList
							);
						}
					} else {
						this.removeClass(this.selectedRouteClass, classList);
					}
				}
			} else {
				//if top level parent is open
				//if menu link, active if matches route
				//is submenu, active and open if a child matches route
				if (this.topLevelParentId === this.mainItemClickedId) {
					if (
						this.isSub &&
						this.itemIdInPath(this.lastMainItemClickedId)
					) {
						const childMatch = this.findChildItem(this.menuItem);

						if (childMatch) {
							this.addClass(this.openClass, classList);
							this.addClass(this.activeClass, classList);
						}
					} else if (this.matchPath(this.menuItem, this.$route)) {
						this.addClass(this.activeClass, classList);
					}
				}
			}

			return classList;
		},
		isMain() {
			const path = this.menuItem.itemValue;
			const test = path.endsWith('MAIN');

			return test;
		},
		isSub() {
			const path = this.menuItem.itemValue;
			const test = path.endsWith('SUB');

			return test;
		},
		currentRouteMatch() {
			let result = false;
			let currentRoute = this.$route.fullPath;

			if (
				this.menuLinkNoBase.toLowerCase() === currentRoute.toLowerCase()
			) {
				result = true;
			}

			return result;
		}
	},
	methods: {
		...mapActions('user', [
			'addToFavoritesAction',
			'removeFromFavoritesAction',
			'orderFavoritesAction',
			'setMainItemClickedAction',
			'setLastItemClickedAction',
			'setMenuIsOpen',
			'setCurrentRouteMenuIdAction',
			'setLastMainItemClickedAction'
		]),
		itemIdInPath(id) {
			let result = false;

			if (
				(this.pathMenuIds && this.pathMenuIds.indexOf(id) >= 0) ||
				this.itemIsChild(id, this.menuItem.childItems)
			) {
				result = true;
			}

			return result;
		},
		itemIsChild(id, childItems) {
			let result = false;

			if (childItems) {
				for (let index = 0; index < childItems.length; index++) {
					const item = childItems[index];

					if (item.id === id) {
						result = true;
						break;
					} else {
						result = this.itemIsChild(id, item.childItems);

						if (result) {
							break;
						}
					}
				}
			}

			return result;
		},
		matchPath(menuItem, route) {
			const idMatch = menuItem.id === this.currentRouteMenuId;
			let routeMatch = false;

			if (idMatch) {
				routeMatch = matchPathUtil(
					getMenuItemLink(menuItem, this.companyId),
					route
				);
			}

			return idMatch && routeMatch;
		},
		onItemsOrderChange(e) {
			const movedItem = this.menuItem.childItems[e.moved.oldIndex];
			const targetItem = this.menuItem.childItems[e.moved.newIndex];
			const newOrder = targetItem.order;

			this.orderFavoritesAction({
				menuItem: movedItem,
				companyId: this.companyId,
				order: newOrder
			});
		},
		openInNewTab() {
			const href = this.menuLink;
			window.open(href, '_blank');
		},
		addToFavorites() {
			this.addToFavoritesAction({
				menuItem: this.menuItem,
				companyId: this.companyId
			});
			this.closeContextMenu();
		},
		removeFromFavorites() {
			this.removeFromFavoritesAction({
				menuItem: this.menuItem,
				companyId: this.companyId
			});
			this.closeContextMenu();
		},
		open() {
			this.isOpen = true;
		},
		close() {
			this.isOpen = false;

			// if (
			// 	this.isMobile &&
			// 	e.srcElement.id !== 'menu-button-icon' &&
			// 	e.srcElement.id !== 'menu-button'
			// ) {
			// 	//this.setMenuIsOpen(false);
			// }
		},
		addClass(className, classList) {
			classList.push(className);
		},
		removeClass(className, classList) {
			const index = classList.indexOf(className);

			if (index >= 0) {
				classList.splice(index, 1);
			}
		},
		handleDocumentClicked(event) {
			const { target } = event;
			const { $el } = this;
			if (!$el.contains(target)) {
				this.close();
			}
		},
		handleAuxClick(e) {
			if (e.button == 1) {
				this.handleClick(e);
			}
		},
		handleClick(e) {
			this.setLastItemClickedAction(this.menuItem);

			if (this.isMain || this.isSub) {
				e.preventDefault();
				this.setLastMainItemClickedAction(this.menuItem.id);

				if (this.isOpen) {
					this.close();
				} else {
					this.open();
				}

				this.transform();
			} else {
				//then we clicked an item that's navigating
				//emit an event that will tell the parent menut items to close
				if (this.menuItem.linkType == 2) {
					if (this.appName === 'vue-ui') {
						//if a limilight page, do a router navigate instead href click
						e.preventDefault();

						if (!this.currentRouteMatch) {
							this.$router.push({ path: this.menuLinkNoBase });
						}
					}
				}

				this.setCurrentRouteMenuIdAction(this.menuItem.id);
				this.setMainItemClickedAction(this.topLevelParentId);

				this.$emit('linkClick');
			}
		},
		showContextMenu(e) {
			if (
				this.menuItem.itemText !== 'Dashboard' &&
				this.menuItem.itemText !== 'MyVUE'
			) {
				e.preventDefault();
				if (this.isMain || this.isSub) {
					return false;
				} else {
					if (!this.contextMenuActive) {
						this.contextMenuActive = true;
					}
				}
			}
		},
		closeContextMenu() {
			this.contextMenuActive = false;
		},
		findChildItem(menuItem) {
			let foundItem;
			let childFoundItem;

			//for top level menu with no children (like Dashboard)
			if (this.matchPath(menuItem, this.$route)) {
				return menuItem;
			}

			//check if we've stored this route in child routes
			//so we don't need to re-do the search
			//NOTE: this will only work when routing -- not when loading pages
			const childRouteIndex = this.childRoutes.findIndex(cr => {
				return this.matchPath(cr, this.$route);
			});

			if (childRouteIndex >= 0) {
				return menuItem;
			}

			if (menuItem.childItems && menuItem.childItems.length > 0) {
				for (
					let index = 0;
					index < menuItem.childItems.length;
					index++
				) {
					let currentItem = menuItem.childItems[index];

					if (this.matchPath(currentItem, this.$route)) {
						foundItem = currentItem;
						break;
					} else if (
						currentItem.childItems &&
						currentItem.childItems.length > 0
					) {
						childFoundItem = this.findChildItem(currentItem);

						if (childFoundItem) {
							foundItem = childFoundItem;
							break;
						}
					}
				}
			}

			if (foundItem) {
				//add the menu item route to the child route collection
				//so we don't need to re-do this search
				if (this.childRoutes.indexOf(foundItem) < 0) {
					this.childRoutes.push(foundItem);
				}
			}

			return foundItem;
		},
		onLinkClick() {
			//then we clicked an item that's navigating
			//emit an event that will tell the parent menut items to close
			this.$emit('linkClick');
			this.close();
		},
		handleResize() {
			clearTimeout(this.resizeId);
			this.resizeId = setTimeout(this.transform, 100);
		},
		transform() {
			this.$nextTick(this.execTransform);
		},
		execTransform() {
			const subMenu = this.$refs.subMenu;
			subMenu.$el.style.transform = 'translateY(0px)';

			if (subMenu && (this.isMain || this.isSub) && this.isOpen) {
				const windowHeight = window.innerHeight;
				const menuBottom = subMenu.$el.getBoundingClientRect().bottom;
				const diff =
					menuBottom - windowHeight > 0
						? menuBottom - windowHeight + 4
						: 0;
				subMenu.$el.style.transform = `translateY(${-diff}px)`;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.vue-side-menu {
	@include mq($from: md-l) {
		// COLLAPSED SIDE MENU - DESKTOP
		&:not(.is-expanded) {
			.root-item.menu-item {
				> .menu-item__link {
					> .menu-item__link-icon {
						margin-right: 0;
					}

					> .menu-item__link-label {
						display: none;
					}

					> .menu-item__expand-icon {
						transition-delay: 0s;
						font-size: 0;
						opacity: 0;
						margin-left: 0;
					}
				}
			}
		}

		// EXPANDED SIDE MENU - DESKTOP
		&.is-expanded {
			@include mq($from: md-l) {
				.root-item.menu-item {
					> .menu-item__link {
						justify-content: flex-start;

						> .menu-item__link-label {
							padding-left: $padding-xs;
						}
					}
				}
			}
		}
	}
}

.vue-side-menu .root-item.menu-item > .menu-item__link {
	padding-left: 13.5px;

	&.is-selected-route {
		> .menu-item__link-icon .icon {
			position: relative;
			color: var(--menu-brand-1-fg-3);

			&::before {
				position: relative;
				z-index: $z-level1-1;
			}

			&::after {
				background: var(--menu-brand-1-selected-bg);
				@include transform-full-center;
				content: '';
				width: 26px;
				height: 26px;
				border-radius: $border-radius;
			}
		}
	}

	@include header-focus-styles;
}

.menu-item {
	font-size: $menu-font-size;
	position: relative;

	&__link {
		display: inline-flex;
		justify-content: flex-start;
		align-items: center;
		width: 100%;
		padding: $padding-sm;
		line-height: 16px;

		&.is-open {
			background: var(--bg-2);
			color: var(--bg-2-fg-3);

			+ .menu-item__sub-menu {
				display: block;
			}

			@include mq($from: md-l) {
				&:hover {
					background: var(--menu-list-bg-hover);
					color: var(--menu-list-fg-hover);
				}
			}
		}

		@include mq($until: md-l) {
			&.is-open {
				background: none;

				.menu-item {
					&__expand-icon {
						transform: rotate(90deg);
						@include animation(all, 'slow', ease);
					}
				}

				+ .menu-item__sub-menu {
					border-left: 2px solid var(--neutral-1);
				}
			}

			&.is-active {
				color: var(--brand-1);

				+ .menu-item__sub-menu {
					background-color: rgba(var(--brand-1_rgb), 0.1);
					border-left: 2px solid var(--brand-1);
				}
			}

			&.is-leaf-parent {
				color: var(--brand-1);
				+ .menu-item__sub-menu {
					border-left: none;

					> .menu-item__sub-menu-item
						.menu-item
						.menu-item__link.is-active {
						background-color: rgba(var(--brand-1_rgb), 0.1);
						color: var(--bg-2-fg-3);
						border-left: 2px solid var(--brand-1);
					}
				}
			}
		}

		@include mq($from: md-l) {
			&.is-leaf-parent {
				+ .menu-item__sub-menu {
					> .menu-item__sub-menu-item
						.menu-item
						.menu-item__link.is-active {
						background-color: var(--bg-2);
						font-weight: $bold-font-weight;

						&:hover {
							background: var(--menu-list-bg-hover);
							color: var(--menu-list-fg-hover);
						}
					}
				}
			}

			&:hover {
				background: var(--menu-list-bg-hover);
				color: var(--menu-list-fg-hover);
			}
		}
	}

	&__link-icon {
		font-size: 20px;
		margin-right: $gutter-md;
	}

	&__link-label {
		display: inline-block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__expand-icon {
		transition-delay: 0.1s;
		transition-property: font-size;
		font-size: 10px;
		width: initial;
		margin-left: auto;
		@include animation(all, 'slow', ease);
	}

	//SUB-MENU
	&__sub-menu {
		margin: 0 $gutter-lg + 4px;
		display: none;
		@include animation(transform, 'slow', ease, 0.15s);

		> &__sub-menu-item {
			border-left: 2px solid var(--neutral-1);
		}

		@include mq($from: md-l) {
			position: absolute;
			top: 0;
			left: 100%;
			width: 200px;
			margin: 0;
			border-left: none;
			background: var(--bg-0);
			border: 1px solid var(--neutral-1);
			@include small-shadow;
		}

		&.is-favorite-parent {
			> .menu-item__sub-menu-item {
				> .menu-item > .menu-item__link {
					cursor: move;
				}

				&.sortable-ghost {
					opacity: 0.2;
				}
			}
			overflow: auto;
			max-height: 75vh;
		}
	}

	&__sub-menu &__link {
		padding-left: $padding-md;
	}

	&__sub-menu &__link-icon {
		display: none;
	}
}
</style>
